<template>
    <div class="bg-white p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-1">Cancel signature request</h3>
        <p class="text-sm text-gray-500 mb-4">
            This pulls the {{ subject }} back so you can correct it. Everyone's current signing link stops working
            immediately, and each {{ recipientNoun }} is emailed to say a new one is coming.
        </p>

        <!-- The consequence a landlord must not be surprised by: cancelling
             voids signatures already collected (RAC-10448). -->
        <div
            v-if="signedNames.length"
            class="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5"
        >
            <svg class="w-4 h-4 text-red-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" /></svg>
            <p class="text-xs text-red-800">
                <span class="font-semibold">{{ signedNames.join(', ') }}</span>
                {{ signedNames.length === 1 ? 'has' : 'have' }} already signed. Cancelling clears
                {{ signedNames.length === 1 ? 'that signature' : 'those signatures' }}, so they will
                need to sign again after you resend.
            </p>
        </div>
        <div v-else class="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5">
            <svg class="w-4 h-4 text-amber-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <p class="text-xs text-amber-800">
                No one has signed yet, so nothing is lost. After cancelling you can edit the {{ subject }} or its
                signers, then send it again.
            </p>
        </div>

        <form @submit.prevent="handleSubmit" class="space-y-4">
            <fieldset class="space-y-2">
                <legend class="block text-sm text-gray-700 mb-1">
                    Why are you cancelling? <span class="text-red-500">*</span>
                </legend>

                <label
                    v-for="option in reasonOptions"
                    :key="option.value"
                    class="flex items-start gap-3 border border-gray-200 rounded-lg px-4 py-3 cursor-pointer hover:border-purple-300"
                    :class="{ 'border-purple-400 bg-purple-50': reason === option.value }"
                >
                    <input
                        v-model="reason"
                        type="radio"
                        name="cancel-reason"
                        :value="option.value"
                        class="mt-0.5 text-purple-600 focus:ring-purple-500"
                    >
                    <span class="text-sm text-gray-800">{{ option.label }}</span>
                </label>
                <p v-if="errors.reason" class="text-xs text-red-600 mt-1">{{ errors.reason }}</p>
            </fieldset>

            <div v-if="reason === 'other'">
                <label for="cancel-reason-note" class="block text-sm text-gray-700 mb-1">
                    Tell us briefly <span class="text-red-500">*</span>
                </label>
                <textarea
                    id="cancel-reason-note"
                    v-model="reasonNote"
                    rows="3"
                    maxlength="500"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                ></textarea>
                <p v-if="errors.reasonNote" class="text-xs text-red-600 mt-1">{{ errors.reasonNote }}</p>
            </div>

            <p v-if="serverError" class="text-sm text-red-600 bg-red-50 p-2 rounded">{{ serverError }}</p>

            <div class="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t border-gray-100">
                <button type="button" @click="$emit('cancel')" class="closeButton">Keep it out for signature</button>
                <button type="submit" class="primaryButton" :disabled="busy" :class="{ 'opacity-25': busy }">
                    {{ busy ? 'Cancelling…' : 'Cancel Signature Request' }}
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
    signers: {
        type: Array,
        default: () => [],
    },
    serverError: {
        type: String,
        default: null,
    },
    busy: {
        type: Boolean,
        default: false,
    },
    // Set for a non-lease document (RAC-10324) — relabels the copy only. Null
    // keeps the lease wording this step shipped with.
    documentName: {
        type: String,
        default: null,
    },
})

const emit = defineEmits(['submit', 'cancel'])

const subject = computed(() => props.documentName || 'lease')

// A lease emails its TENANTS only — the landlord is the one cancelling, and at
// the countersign stage they are themselves an invited signer. A document has no
// such roles, so every signer hears about it.
const recipientNoun = computed(() => (props.documentName ? 'signer' : 'tenant'))

// The VALUES are fixed by the server (CancelLeaseSignatureRequest /
// CancelDocumentSignatureRequest share them, so support can group cancellations
// across both kinds); only the labels follow the document type.
const reasonOptions = computed(() => [
    {
        value: 'edit_lease',
        label: props.documentName ? 'I need to edit the document' : 'I need to edit the lease',
    },
    {
        value: 'edit_tenants',
        label: props.documentName ? 'I need to edit the signers' : 'I need to edit the tenants',
    },
    { value: 'other', label: 'Something else' },
])

const reason = ref(null)
const reasonNote = ref('')
const errors = ref({})

const signedNames = computed(
    () => (props.signers || [])
        .filter((signer) => signer.status === 'signed')
        .map((signer) => signer.name)
        .filter(Boolean)
)

const handleSubmit = () => {
    const found = {}
    if (!reason.value) {
        found.reason = 'Choose a reason for cancelling.'
    }
    if (reason.value === 'other' && !reasonNote.value.trim()) {
        found.reasonNote = 'Please tell us briefly why you are cancelling.'
    }
    errors.value = found

    if (Object.keys(found).length > 0) {
        return
    }

    emit('submit', {
        reason: reason.value,
        reason_note: reason.value === 'other' ? reasonNote.value.trim() : null,
    })
}
</script>
